import {
  Accordion,
  DomElementAlignment,
  DomTargetPosition,
  IconButton,
  OdinIcon,
  OdinIconSize,
  OverlayPanel,
} from '@myosh/odin-components';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetAnalysisJobStatusApiResponse } from '../../@types/background-jobs';
import type { CloneRecordsApiProps, ExportRecordFieldApiProps } from '../../@types/records';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useDeleteBackgroundJobMutation } from '../../redux/services/record';
import type {
  AnalyzeRecordsBackgroundJobApiProps,
  BackgroundJobDetails,
  ExportFullRecordsBackgroundJobApiProps,
  ExportRecordsBackgroundJobApiProps,
  ExportSingleRecordBackgroundJobApiProps,
  FileBackgroundJobDetails,
  ImportFormBackgroundJobDetails,
  RecordBackgroundJobDetails,
} from '../../redux/slices/background-job-slice';
import {
  JobStatus,
  JobType,
  removeBackgroundJob,
  revertRemoveBackgroundJob,
  selectIsPanelOpen,
  setIsPanelOpen,
  selectJobsForBackgroundJobPanel,
} from '../../redux/slices/background-job-slice';
import { forceAssert } from '../../utils/type-guards';
import AnalyzeRecordBackgroundJobItem from './analyze-record-background-job-item.component';
import FileBackgroundJobItem from './file-background-job-item.component';
import FormBackgroundJobItem from './form-background-job-item.component';
import RecordBackgroundJobItem from './record-background-job-item.component';
import RecordFieldBackgroundJobItem from './record-field-background-job-item.component';
import BaseJobPanelItemComponent from './base-job-panel-item.component';

const exportJobs = [
  JobType.ExportSingleRecord,
  JobType.ExportRecords,
  JobType.ExportFullRecords,
  JobType.ExportRecordField,
];
const recordJobs = [JobType.CloneRecords];
const analysisJobs = [JobType.AnalyzeRecords, JobType.ImportForm];
const importJobs = [JobType.ImportAttachments];

export interface BackgroundJobPanelItemProps<T, U> {
  job: BackgroundJobDetails<T, U>;
  onRemove: () => void;
}

function BackgroundJobPanel() {
  const exportPanelButtonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();

  const jobsForBackgroundJobPanel = useAppSelector(selectJobsForBackgroundJobPanel);
  const isPanelOpen = useAppSelector(selectIsPanelOpen);
  const dispatch = useAppDispatch();

  const [deleteBackgroundJob] = useDeleteBackgroundJobMutation();

  const onJobRemove = useCallback((id: string, backgroundJobId?: number) => {
    if (id) {
      // optimistic update
      dispatch(removeBackgroundJob(id));

      if (backgroundJobId) {
        deleteBackgroundJob(backgroundJobId).catch(() => {
          // revert optimistic update
          dispatch(revertRemoveBackgroundJob(id));
        });
      }
    }
  }, []);

  const onExportPanelButtonClick = useCallback(() => dispatch(setIsPanelOpen(!isPanelOpen)), [isPanelOpen]);
  const closePanel = useCallback(() => dispatch(setIsPanelOpen(false)), []);

  const getPanelButtonStatusIcon = useCallback(() => {
    const statusIconClasses = 'absolute -top-2 -right-2';

    if (jobsForBackgroundJobPanel.some((job) => job.status === JobStatus.InProgress)) {
      return <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} className={statusIconClasses} spin />;
    } else {
      return null;
    }
  }, [jobsForBackgroundJobPanel]);

  const backgroundJobs = (jobType: number[]) => {
    return jobsForBackgroundJobPanel
      .filter((job) => jobType.includes(job.type))
      .map((job) => {
        const handleRemove = () => onJobRemove(job.id, job.details?.backgroundJobId as number);

        if (job.type === JobType.ExportSingleRecord) {
          return (
            <FileBackgroundJobItem
              key={job.id}
              job={forceAssert<BackgroundJobDetails<ExportSingleRecordBackgroundJobApiProps, FileBackgroundJobDetails>>(
                job
              )}
              onRemove={handleRemove}
            />
          );
        } else if (job.type === JobType.ExportRecords) {
          return (
            <FileBackgroundJobItem
              key={job.id}
              job={forceAssert<BackgroundJobDetails<ExportRecordsBackgroundJobApiProps, FileBackgroundJobDetails>>(job)}
              onRemove={handleRemove}
            />
          );
        } else if (job.type === JobType.ExportFullRecords) {
          return (
            <FileBackgroundJobItem
              key={job.id}
              job={forceAssert<BackgroundJobDetails<ExportFullRecordsBackgroundJobApiProps, FileBackgroundJobDetails>>(
                job
              )}
              onRemove={handleRemove}
            />
          );
        } else if (job.type === JobType.ExportRecordField) {
          return (
            <RecordFieldBackgroundJobItem
              key={job.id}
              job={forceAssert<BackgroundJobDetails<ExportRecordFieldApiProps, FileBackgroundJobDetails>>(job)}
              onRemove={handleRemove}
            />
          );
        } else if (job.type === JobType.CloneRecords) {
          return (
            <RecordBackgroundJobItem
              key={job.id}
              job={forceAssert<BackgroundJobDetails<CloneRecordsApiProps, RecordBackgroundJobDetails>>(job)}
              onRemove={handleRemove}
            />
          );
        } else if (job.type === JobType.AnalyzeRecords) {
          return (
            <AnalyzeRecordBackgroundJobItem
              key={job.id}
              job={forceAssert<
                BackgroundJobDetails<AnalyzeRecordsBackgroundJobApiProps, GetAnalysisJobStatusApiResponse>
              >(job)}
              onRemove={handleRemove}
            />
          );
        } else if (job.type === JobType.ImportForm) {
          return (
            <FormBackgroundJobItem
              key={job.id}
              job={forceAssert<BackgroundJobDetails<unknown, ImportFormBackgroundJobDetails>>(job)}
              onRemove={handleRemove}
            />
          );
        } else if (job.type === JobType.ImportAttachments) {
          return (
            <BaseJobPanelItemComponent
              key={job.id}
              icon={<OdinIcon icon="FileText" />}
              title={t('import-attachments')}
              status={job.status}
              onRemove={handleRemove}
            />
          );
        }
      });
  };

  const jobAmountPerType = (jobType: number[]) => {
    return jobsForBackgroundJobPanel.filter((job) => jobType.includes(job.type)).length ?? 0;
  };

  const accordionGroups = [
    {
      isOpen: jobAmountPerType(exportJobs) > 0,
      key: 'Exports',
      children: backgroundJobs(exportJobs),
      hidden: jobAmountPerType(exportJobs) === 0,
      title: (
        <div className="flex flex-row items-center justify-between px-2">
          <div className="flex items-center">
            <OdinIcon icon="Download" size={OdinIconSize.ExtraSmall} className="mr-2" />
            <p className="text-base">{t('exports')}</p>
          </div>
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-5 text-sm">
            {jobAmountPerType(exportJobs)}
          </div>
        </div>
      ),
    },
    {
      isOpen: jobAmountPerType(importJobs) > 0,
      key: 'Imports',
      children: backgroundJobs(importJobs),
      hidden: jobAmountPerType(importJobs) === 0,
      title: (
        <div className="flex flex-row items-center justify-between px-2">
          <div className="flex items-center">
            <OdinIcon icon="Upload" size={OdinIconSize.ExtraSmall} className="mr-2" />
            <p className="text-base">{t('import')}</p>
          </div>
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-5 text-sm">
            {jobAmountPerType(importJobs)}
          </div>
        </div>
      ),
    },
    {
      isOpen: jobAmountPerType(recordJobs) > 0,
      key: 'Clones',
      children: backgroundJobs(recordJobs),
      hidden: jobAmountPerType(recordJobs) === 0,
      title: (
        <div className="flex flex-row items-center justify-between px-2">
          <div className="flex items-center">
            <OdinIcon icon="Copy" size={OdinIconSize.ExtraSmall} className="mr-2" />
            <p className="text-base">{t('clones')}</p>
          </div>
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-5 text-sm">
            {jobAmountPerType(recordJobs)}
          </div>
        </div>
      ),
    },
    {
      isOpen: jobAmountPerType(analysisJobs) > 0,
      hidden: jobAmountPerType(analysisJobs) === 0,
      key: 'AI Analysis',
      children: backgroundJobs(analysisJobs),
      title: (
        <div className="flex flex-row items-center justify-between px-2">
          <div className="flex items-center">
            <OdinIcon icon="FlaskConical" size={OdinIconSize.ExtraSmall} className="mr-2" />
            <p className="text-base">{t('ai-analysis')}</p>
          </div>
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-5 text-sm">
            {jobAmountPerType(analysisJobs)}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      {jobsForBackgroundJobPanel.length > 0 && (
        <div className="relative h-6 w-6">
          <button ref={exportPanelButtonRef} onClick={onExportPanelButtonClick} className="relative">
            <OdinIcon icon="Bell" size={OdinIconSize.Small} />
            {getPanelButtonStatusIcon()}
          </button>
          <OverlayPanel
            visible={isPanelOpen}
            target={exportPanelButtonRef.current}
            targetPosition={DomTargetPosition.BottomRight}
            elementAlignment={DomElementAlignment.TopRight}
            hidden={closePanel}
            className="max-h-96 w-80 rounded-lg bg-mono-1 text-mono-0 shadow-lg"
            shouldCheckZIndex
          >
            <div className="flex items-center justify-between border-b border-solid border-gray-4 bg-mono-1 px-2 py-3 text-lg font-semibold leading-9">
              <span className="whitespace-nowrap">{t('activity-centre')}</span>
              <IconButton icon="X" onClick={closePanel} />
            </div>
            <div className="rounded-b-lg bg-mono-1">
              {accordionGroups.map((item) => {
                return (
                  <Accordion
                    key={item.key}
                    id={item.key}
                    isOpen={item.isOpen}
                    title={item.title}
                    headerStyles="py-3 focus:outline-none focus:mb-0 border-b border-solid border-gray-4 focus:border-gray-4 focus:border-b focus:border-solid shadow"
                    childWrapperStyles="border-b border-solid border-gray-4 pt-0"
                    wrapperStyles="mb-0"
                    hidden={item.hidden}
                  >
                    <div className="custom-scroll max-h-40 overflow-y-auto">{item.children}</div>
                  </Accordion>
                );
              })}
            </div>
          </OverlayPanel>
        </div>
      )}
    </>
  );
}

export default React.memo(BackgroundJobPanel);
